// MicListeningOverlay — voice-assistant-style full-screen overlay
// rendered while the mic is active. Replaces in-line partial-text
// rendering: the user sees a big pulsing indicator instead of
// flickering streaming text. Final transcript commits via the existing
// onStreamFinal callback and the overlay dismisses.
//
// States:
//   "streaming"      → "Listening…" with audio-level-driven pulse
//   "transcribing"   → "Transcribing…" with a spinner (brief; the
//                       window between vad_stopped/user-stop and the
//                       server's final message arriving)
//
// Position fixed, z-index high enough to cover any in-app modal stack.
// Tapping the X cancels (no final commit); tapping the mic stops
// (waits for final + commits, same as user-side stop).

import { Icon } from "../icons";
import "./mic-listening-overlay.css";

/**
 * Synthesizes a short chime via Web Audio API. Two sine tones with
 * gentle envelopes — no asset bundle, no fetch, works offline.
 *
 * playStartChime: rising E5 → B5 on overlay mount. Confirms mic is
 *   actively listening.
 * playEndChime:   descending B5 → E5 when state transitions to
 *   "transcribing". Sonic bookend confirming we heard the request.
 */
// Singleton AudioContext — created lazily on first chime, kept alive
// across plays. Creating a fresh context per chime worked for the
// END chime (which fires after user interaction) but the START chime
// often missed because the new context spun up too late to honor the
// scheduled timestamps.
type WindowWithLegacyAudio = Window & { webkitAudioContext?: typeof AudioContext };
let sharedCtx: AudioContext | null = null;

async function getCtx(): Promise<AudioContext | null> {
  if (sharedCtx && sharedCtx.state !== "closed") {
    if (sharedCtx.state === "suspended") {
      try { await sharedCtx.resume(); } catch { /* swallow */ }
    }
    return sharedCtx;
  }
  const AudioCtx = window.AudioContext || (window as WindowWithLegacyAudio).webkitAudioContext;
  if (!AudioCtx) return null;
  sharedCtx = new AudioCtx();
  if (sharedCtx.state === "suspended") {
    try { await sharedCtx.resume(); } catch { /* swallow */ }
  }
  return sharedCtx;
}

async function playChime(notes: { freq: number; start: number; dur: number }[]) {
  try {
    const ctx = await getCtx();
    if (!ctx) { console.warn("[chime] no AudioContext"); return; }
    console.log("[chime] play, ctx.state=", ctx.state);
    // Schedule slightly into the future so the first sample lands after
    // any audio-graph latency. 60ms is small enough not to feel laggy
    // but big enough to survive the worst-case scheduling delay we saw
    // when starting from a freshly-resumed context.
    const base = ctx.currentTime + 0.06;
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.freq, base + n.start);
      osc.connect(gain).connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, base + n.start);
      gain.gain.exponentialRampToValueAtTime(0.32, base + n.start + 0.012);
      gain.gain.setValueAtTime(0.32, base + n.start + n.dur * 0.4);
      gain.gain.exponentialRampToValueAtTime(0.0001, base + n.start + n.dur);
      osc.start(base + n.start);
      osc.stop(base + n.start + n.dur + 0.02);
    }
    // Don't close — shared context. Per-chime oscillators auto-clean.
  } catch { /* audio start can fail without user gesture — best-effort */ }
}

export const playStartChime = () => playChime([
  { freq: 659.25, start: 0,    dur: 0.08 }, // E5
  { freq: 987.77, start: 0.07, dur: 0.11 }, // B5
]);

export const playEndChime = () => playChime([
  { freq: 987.77, start: 0,    dur: 0.07 }, // B5
  { freq: 659.25, start: 0.06, dur: 0.10 }, // E5 — mirror of start
]);

interface MicListeningOverlayProps {
  /** "streaming" | "transcribing" — anything else means the overlay
   *  shouldn't render. */
  state: "streaming" | "transcribing";
  /** RMS level in [0, 1] from the native bridge; drives the pulse
   *  radius during "streaming". Unused during "transcribing". */
  level: number;
  /** Politely end the session and commit the final transcript. */
  onStop: () => void;
  /** Throw away the in-flight session without committing. */
  onCancel: () => void;
  /** Live STT partial transcript — shown below the pulse so the user
   *  sees their words landing in real time, killing the "did it hear
   *  me?" anxiety while the VAD silence timeout drains. */
  partialText?: string;
}

export function MicListeningOverlay({ state, level, onStop, onCancel, partialText }: MicListeningOverlayProps) {
  const isStreaming = state === "streaming";

  // Chime triggers live in quick-chat.tsx now (parent component), so
  // they fire on EVERY idle→streaming transition — including the
  // auto-follow-up reopen where this overlay doesn't remount because
  // the sheet is showing instead. Keeping chime here would have meant
  // silent follow-ups, which left the user unsure whether the mic
  // had actually reopened.
  // Smoothed level → a pulse radius the user can read at a glance.
  // Floor at 0.15 so even silent moments show some presence; ceiling
  // at 1.0. Scale to 80px → 220px radius range.
  const clamped = Math.max(0.15, Math.min(1, level));
  const ringPx = 80 + clamped * 140;
  return (
    <div
      className="mic-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isStreaming ? "Listening" : "Transcribing"}
    >
      <button
        className="mic-overlay-cancel icon-btn"
        onClick={onCancel}
        title="Cancel"
        type="button"
      >
        <Icon name="x" size={24} />
      </button>

      <div className="mic-overlay-stage">
        {isStreaming ? (
          <div
            className="mic-overlay-pulse"
            style={{ "--pulse-size": `${ringPx}px` } as Record<string, string | number>}
          >
            <div className="mic-overlay-pulse-core">
              {/* Vulture silhouette via CSS mask so it picks up
                * --bg (the overlay's accent-on-accent contrast color)
                * automatically. Matches the FAB branding so the
                * "FAB → overlay" transition feels like the same bird
                * leaning in to listen. */}
              <span className="mic-overlay-vulture" aria-hidden="true" />
            </div>
          </div>
        ) : (
          <div className="mic-overlay-spinner" aria-hidden="true">
            <div /><div /><div />
          </div>
        )}
        <div className="mic-overlay-label">
          {isStreaming ? "Listening…" : "Transcribing…"}
        </div>
        {partialText && (
          <div className="mic-overlay-partial" aria-live="polite">
            {partialText}
          </div>
        )}
      </div>

      {isStreaming && (
        <button
          className="btn btn-primary mic-overlay-stop"
          onClick={onStop}
          type="button"
        >
          <Icon name="check" size={16} />
          <span>Done</span>
        </button>
      )}
    </div>
  );
}
